/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Vault-passphrase recovery wizard (#190).
 *
 * Mounted as a sibling of `TdVaultLock` inside `TdDashboard`. Listens for the
 * `forgot-passphrase` event (dispatched by `TdVaultLock`'s "Forgot passphrase?"
 * link) and opens a `<wa-dialog>` containing a multi-step wizard.
 *
 * Input modes (#190 shipped paste; #246 added camera + upload):
 *   - paste   — textarea for the base64nopad recovery blob
 *   - camera  — live QR scan via getUserMedia + `qrDecode.ts`
 *   - upload  — decode a photo/scan of the printed page via `qrDecode.ts`
 *
 * Camera and upload both feed the decoded QR payload through the SAME
 * paste-path validation (`_decodeBundle`) — a scanned bundle is checked
 * exactly like a pasted one.
 *
 * Flow:
 *   1. mode-select   — radio choices for the three input modes
 *   2. paste-input   — textarea for the base64nopad recovery blob
 *      camera-scan   — live video preview, polls frames for a QR code
 *      upload-input  — file picker for an image of the printed page
 *   3. submitting    — POST to /api/auth/recover-passphrase
 *   4a. success      — show the recovered passphrase + "Use this passphrase"
 *   4b. failure      — error message + retry/cancel
 *
 * On "Use this passphrase", dispatches `passphrase-recovered` (bubbles,
 * composed) with `detail: { passphrase: string }`. `TdVaultLock` listens for
 * this on `document` and fills + unlocks.
 *
 * Encryption side: `src/components/TdPrintQr.js`.
 * Recovery client: `src/services/recovery.ts`.
 * Backend contract: beyonddemise/app-backend#94.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { base64nopad } from '@scure/base';
import {
  RecoveryBundleSchema,
  type RecoveryBundle,
} from '../services/crypto/escrow.js';
import {
  recoverPassphrase,
  RecoveryServiceError,
} from '../services/recovery.js';
import { decodeQrFromBlob, decodeQrFromVideoFrame } from './qrDecode.js';
import { t, LocaleController } from '../services/i18n.js';

type WizardStep =
  | 'mode-select'
  | 'paste-input'
  | 'camera-scan'
  | 'upload-input'
  | 'submitting'
  | 'success'
  | 'failure';

type InputMode = 'paste' | 'camera' | 'upload';

@customElement('td-forgot-pass')
export class TdForgotPass extends LitElement {
  @property({ type: Boolean }) open = false;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  @state() private step: WizardStep = 'mode-select';
  @state() private mode: InputMode = 'paste';
  @state() private pastedText = '';
  @state() private recoveredPassphrase: string | null = null;
  @state() private failureMessage: string | null = null;
  @state() private cameraError: string | null = null;
  @state() private uploadError: string | null = null;

  private _forgotPassListener?: (event: Event) => void;
  /** Live camera stream while the camera-scan step is active. */
  private _stream: MediaStream | null = null;
  /** Frame-polling timer for the camera-scan step. */
  private _scanTimer: ReturnType<typeof setInterval> | null = null;
  /** Re-entrancy guard — skip a tick while a frame decode is in flight. */
  private _scanBusy = false;
  /**
   * Latches when a final event (cancel or recovered) has been dispatched for
   * the current "show" cycle. `wa-dialog`'s close animation fires `wa-hide`
   * AFTER the close button / programmatic `open = false` path, which would
   * cause a second `forgot-pass-cancelled` to fire (or a misleading cancel
   * AFTER a successful `passphrase-recovered`). The latch is reset by
   * `show()` so the next open cycle starts clean.
   */
  private _finalEmitted = false;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Listen for the open-wizard signal dispatched by TdVaultLock.
    this._forgotPassListener = () => this.show();
    document.addEventListener('forgot-passphrase', this._forgotPassListener);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stopCamera();
    if (this._forgotPassListener) {
      document.removeEventListener(
        'forgot-passphrase',
        this._forgotPassListener
      );
      delete this._forgotPassListener;
    }
  }

  /**
   * Open the wizard at step 1. Safe to call repeatedly — resets state, the
   * final-emission latch, and any leftover camera stream.
   */
  show(): void {
    this._stopCamera();
    this.step = 'mode-select';
    this.mode = 'paste';
    this.pastedText = '';
    this.recoveredPassphrase = null;
    this.failureMessage = null;
    this.cameraError = null;
    this.uploadError = null;
    this._finalEmitted = false;
    this.open = true;
  }

  /**
   * Close the dialog, release the camera, and emit `forgot-pass-cancelled`
   * (once per show cycle). Idempotent — repeat calls (e.g. wa-hide firing
   * after a programmatic close) are suppressed by the `_finalEmitted` latch.
   */
  private _close = (): void => {
    this._stopCamera();
    if (this._finalEmitted) {
      this.open = false;
      return;
    }
    this._finalEmitted = true;
    this.open = false;
    this.dispatchEvent(
      new CustomEvent('forgot-pass-cancelled', {
        bubbles: true,
        composed: true,
      })
    );
  };

  private _handleModeContinue = (): void => {
    switch (this.mode) {
      case 'paste':
        this.step = 'paste-input';
        break;
      case 'camera':
        this.step = 'camera-scan';
        void this._startCamera();
        break;
      case 'upload':
        this.step = 'upload-input';
        break;
    }
  };

  private _handleModeChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | null;
    if (!target) return;
    if (
      target.value === 'paste' ||
      target.value === 'camera' ||
      target.value === 'upload'
    ) {
      this.mode = target.value;
    }
  };

  // ---------------------------------------------------------------------
  // Camera-scan mode (#246)
  // ---------------------------------------------------------------------

  /**
   * Request camera access and start polling frames for a QR code. Sets
   * `cameraError` (rendered inside the camera-scan step) on permission
   * denial, missing hardware, or an unsupported browser.
   */
  private async _startCamera(): Promise<void> {
    this.cameraError = null;
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.getUserMedia) {
      this.cameraError = t('forgotpass.error.cameraUnsupported');
      return;
    }
    try {
      // High ideal resolution: the escrow QR is dense (version ≈ 40), so
      // low-res frames rarely decode.
      this._stream = await mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
    } catch (error) {
      this.cameraError = this._cameraErrorMessageFor(error);
      return;
    }
    // The user may have navigated away while the permission prompt was up.
    if (this.step !== 'camera-scan' || !this.isConnected) {
      this._stopCamera();
      return;
    }
    await this._attachStream();
    this._scanTimer = setInterval(() => void this._scanFrame(), 500);
  }

  /** Attach the live stream to the rendered `<video>` element. */
  private async _attachStream(): Promise<void> {
    await this.updateComplete;
    const video = this.querySelector<HTMLVideoElement>('#td-forgot-pass-video');
    if (!video || !this._stream) return;
    video.srcObject = this._stream;
    try {
      await video.play();
    } catch {
      // Autoplay rejection — the element is muted + playsinline, so this
      // only happens in exotic configurations; polling simply finds no
      // frames and the user can go back.
    }
  }

  /** One polling tick: grab a frame, try to decode, submit on success. */
  private _scanFrame = async (): Promise<void> => {
    if (this._scanBusy || this.step !== 'camera-scan') return;
    const video = this.querySelector<HTMLVideoElement>('#td-forgot-pass-video');
    if (!video) return;
    this._scanBusy = true;
    try {
      const payload = await decodeQrFromVideoFrame(video);
      if (payload && this.step === 'camera-scan') {
        this._stopCamera();
        await this._submitPayload(payload);
      }
    } finally {
      this._scanBusy = false;
    }
  };

  /** Stop frame polling and release all camera tracks. Idempotent. */
  private _stopCamera(): void {
    if (this._scanTimer !== null) {
      clearInterval(this._scanTimer);
      this._scanTimer = null;
    }
    if (this._stream) {
      for (const track of this._stream.getTracks()) track.stop();
      this._stream = null;
    }
  }

  /** Map a getUserMedia rejection onto a user-facing message. */
  private _cameraErrorMessageFor(error: unknown): string {
    const name = error instanceof DOMException ? error.name : '';
    switch (name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return t('forgotpass.error.cameraDenied');
      case 'NotFoundError':
      case 'OverconstrainedError':
        return t('forgotpass.error.cameraNotFound');
      case 'NotReadableError':
        return t('forgotpass.error.cameraBusy');
      default:
        return t('forgotpass.error.cameraFailed');
    }
  }

  // ---------------------------------------------------------------------
  // Upload mode (#246)
  // ---------------------------------------------------------------------

  private _handleUploadClick = (): void => {
    this.querySelector<HTMLInputElement>('#td-forgot-pass-file')?.click();
  };

  /** Decode the selected image file; inline error when no QR is found. */
  private _handleFileSelected = async (event: Event): Promise<void> => {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (input) input.value = ''; // allow re-selecting the same file
    if (!file) return;
    this.uploadError = null;
    const payload = await decodeQrFromBlob(file);
    if (!payload) {
      this.uploadError = t('forgotpass.error.noQr');
      return;
    }
    await this._submitPayload(payload);
  };

  /**
   * Feed a decoded QR payload (camera or upload) through the existing
   * paste-path validation + submit. The payload lands in `pastedText`, so
   * a failed attempt drops the user back into the paste step with the
   * decoded text visible and editable.
   */
  private async _submitPayload(payload: string): Promise<void> {
    this.pastedText = payload;
    await this._handleSubmit();
  }

  private _handlePasteInput = (event: Event): void => {
    const target = event.target as { value?: unknown } | null;
    this.pastedText =
      target && typeof target.value === 'string' ? target.value : '';
  };

  /**
   * Decode the pasted backup-text blob into a `RecoveryBundle`.
   *
   * The wire format from TdPrintQr is `base64nopad(utf8(JSON.stringify(bundle)))`.
   * The printed page may add whitespace (4-letter chunks + 15 spaces +
   * newlines from `TdPrintQr.formatSourceText`) — strip any whitespace before
   * decoding so the user can paste the formatted text directly.
   *
   * @returns the parsed bundle, or `null` if decoding fails at any stage
   */
  private _decodeBundle(raw: string): RecoveryBundle | null {
    const stripped = raw.replace(/\s+/g, '');
    if (!stripped) return null;
    try {
      const bytes = base64nopad.decode(stripped);
      const json = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      const obj = JSON.parse(json);
      const parsed = RecoveryBundleSchema.safeParse(obj);
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }

  private _handleSubmit = async (): Promise<void> => {
    const bundle = this._decodeBundle(this.pastedText);
    if (!bundle) {
      this.failureMessage = t('forgotpass.error.invalidBlob');
      this.step = 'failure';
      return;
    }

    this.step = 'submitting';
    try {
      const passphrase = await recoverPassphrase(bundle);
      this.recoveredPassphrase = passphrase;
      this.step = 'success';
    } catch (error) {
      this.failureMessage = this._failureMessageFor(error);
      this.step = 'failure';
    }
  };

  private _failureMessageFor(error: unknown): string {
    if (error instanceof RecoveryServiceError) {
      switch (error.reason) {
        case 'authentication-failed':
          return t('forgotpass.error.authFailed');
        case 'unauthorized':
          return t('forgotpass.error.sessionExpired');
        case 'rate-limited':
          return t('forgotpass.error.rateLimited');
        case 'malformed-bundle':
          return t('forgotpass.error.malformedBundle');
        case 'malformed-response':
          return t('forgotpass.error.malformedResponse');
        case 'server-error':
          return t('forgotpass.error.serverError');
        case 'network-error':
          return t('forgotpass.error.networkError');
      }
    }
    return t('forgotpass.error.unexpected');
  }

  private _handleRetry = (): void => {
    this.failureMessage = null;
    this.step = 'paste-input'; // preserve `pastedText` so user can edit
  };

  private _handleUsePassphrase = (): void => {
    if (!this.recoveredPassphrase) return;
    // Latch BEFORE setting `open = false` so the wa-hide handler doesn't
    // fire a misleading `forgot-pass-cancelled` after a successful recovery.
    this._finalEmitted = true;
    this.dispatchEvent(
      new CustomEvent('passphrase-recovered', {
        bubbles: true,
        composed: true,
        detail: { passphrase: this.recoveredPassphrase },
      })
    );
    this.open = false;
  };

  private _handleBackToModeSelect = (): void => {
    this._stopCamera();
    this.cameraError = null;
    this.uploadError = null;
    this.step = 'mode-select';
  };

  // ---------------------------------------------------------------------
  // Render helpers — one per wizard step.
  // ---------------------------------------------------------------------

  private _renderModeSelect(): TemplateResult {
    return html`
      <div class="td-forgot-pass-step" data-step="mode-select">
        <p>${t('forgotpass.modeQuestion')}</p>
        <wa-radio-group
          name="recovery-mode"
          value=${this.mode}
          @input=${this._handleModeChange}
        >
          <wa-radio value="paste">${t('forgotpass.mode.paste')}</wa-radio>
          <wa-radio value="camera">${t('forgotpass.mode.camera')}</wa-radio>
          <wa-radio value="upload">${t('forgotpass.mode.upload')}</wa-radio>
        </wa-radio-group>
        <div class="td-forgot-pass-actions">
          <wa-button variant="brand" @click=${this._handleModeContinue}>
            ${t('common.continue')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._close}>
            ${t('common.cancel')}
          </wa-button>
        </div>
      </div>
    `;
  }

  private _renderPasteInput(): TemplateResult {
    return html`
      <div class="td-forgot-pass-step" data-step="paste-input">
        <p>${t('forgotpass.pasteIntro')}</p>
        <wa-textarea
          id="td-forgot-pass-paste"
          rows="6"
          .value=${this.pastedText}
          @input=${this._handlePasteInput}
        ></wa-textarea>
        <div class="td-forgot-pass-actions">
          <wa-button
            variant="brand"
            @click=${this._handleSubmit}
            ?disabled=${this.pastedText.trim().length === 0}
          >
            ${t('forgotpass.recover')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._handleBackToModeSelect}>
            ${t('common.back')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._close}>
            ${t('common.cancel')}
          </wa-button>
        </div>
      </div>
    `;
  }

  private _renderCameraScan(): TemplateResult {
    return html`
      <div class="td-forgot-pass-step" data-step="camera-scan">
        ${
          this.cameraError
            ? html`<wa-callout variant="danger"
                >${this.cameraError}</wa-callout
              >`
            : html`
                <p>${t('forgotpass.cameraPoint')}</p>
                <video
                  id="td-forgot-pass-video"
                  style="width: 100%; max-height: 60vh; background: #000;"
                  autoplay
                  muted
                  playsinline
                ></video>
                <p>${t('forgotpass.scanning')}</p>
              `
        }
        <div class="td-forgot-pass-actions">
          <wa-button variant="neutral" @click=${this._handleBackToModeSelect}>
            ${t('common.back')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._close}>
            ${t('common.cancel')}
          </wa-button>
        </div>
      </div>
    `;
  }

  private _renderUploadInput(): TemplateResult {
    return html`
      <div class="td-forgot-pass-step" data-step="upload-input">
        <p>${t('forgotpass.uploadIntro')}</p>
        ${
          this.uploadError
            ? html`<wa-callout variant="warning"
                >${this.uploadError}</wa-callout
              >`
            : nothing
        }
        <input
          id="td-forgot-pass-file"
          type="file"
          accept="image/*"
          hidden
          @change=${this._handleFileSelected}
        />
        <div class="td-forgot-pass-actions">
          <wa-button variant="brand" @click=${this._handleUploadClick}>
            ${t('forgotpass.chooseImage')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._handleBackToModeSelect}>
            ${t('common.back')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._close}>
            ${t('common.cancel')}
          </wa-button>
        </div>
      </div>
    `;
  }

  private _renderSubmitting(): TemplateResult {
    return html`
      <div class="td-forgot-pass-step" data-step="submitting">
        <p>${t('forgotpass.submitting')}</p>
        <wa-progress-bar indeterminate></wa-progress-bar>
      </div>
    `;
  }

  private _renderSuccess(): TemplateResult {
    const words = (this.recoveredPassphrase ?? '').split(/\s+/).filter(Boolean);
    return html`
      <div class="td-forgot-pass-step" data-step="success">
        <p>${t('forgotpass.successIntro')}</p>
        <ol class="td-forgot-pass-words">
          ${words.map(w => html`<li>${w}</li>`)}
        </ol>
        <p>
          ${t('forgotpass.successHint1')}
          <strong>${t('forgotpass.successHintStrong')}</strong>
          ${t('forgotpass.successHint2')}
        </p>
        <div class="td-forgot-pass-actions">
          <wa-button variant="brand" @click=${this._handleUsePassphrase}>
            ${t('forgotpass.usePassphrase')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._close}>
            ${t('common.done')}
          </wa-button>
        </div>
      </div>
    `;
  }

  private _renderFailure(): TemplateResult {
    return html`
      <div class="td-forgot-pass-step" data-step="failure">
        <wa-callout variant="danger">
          ${this.failureMessage ?? t('forgotpass.recoveryFailed')}
        </wa-callout>
        <div class="td-forgot-pass-actions">
          <wa-button variant="brand" @click=${this._handleRetry}>
            ${t('forgotpass.tryAgain')}
          </wa-button>
          <wa-button variant="neutral" @click=${this._close}>
            ${t('common.cancel')}
          </wa-button>
        </div>
      </div>
    `;
  }

  private _renderStep(): TemplateResult | typeof nothing {
    switch (this.step) {
      case 'mode-select':
        return this._renderModeSelect();
      case 'paste-input':
        return this._renderPasteInput();
      case 'camera-scan':
        return this._renderCameraScan();
      case 'upload-input':
        return this._renderUploadInput();
      case 'submitting':
        return this._renderSubmitting();
      case 'success':
        return this._renderSuccess();
      case 'failure':
        return this._renderFailure();
      default:
        return nothing;
    }
  }

  override render(): TemplateResult {
    return html`
      <wa-dialog
        label=${t('forgotpass.dialogLabel')}
        ?open=${this.open}
        light-dismiss
        @wa-hide=${this._close}
      >
        ${this._renderStep()}
      </wa-dialog>
    `;
  }
}
